'use client';

import { useState } from 'react';
import { useFormState, useWatch } from 'react-hook-form';
import { type Country } from 'react-phone-number-input';
import { Plus } from 'lucide-react';
import {
  Button,
  Card,
  Checkbox,
  Input,
  Separator,
} from '@mokoboko/components';

import SmartPhoneInput from '@/components/smart-phone-input';
import { guestCardTitle, guestTermsErrorMessage } from '@/helpers/guest-pass';
import { GUEST_TERMS_LINE, type TermsTagValues } from '@/helpers/terms';
import type { GuestDetailsForm } from '@/hooks/use-guest-details-form';
import { TrashDeleteIcon } from '@/icons';
import { ALLOWED_COUNTRIES } from '@/lib/constants';
import { mergeClassNames } from '@/lib/utils';
import type { ResolvedTerms } from '@/services/terms';
import { TermsDocLinks } from './terms-acceptance';
import { TermsModal } from './terms-modal';

// The browser's autofill would put the host's own details into a guest's
// fields, and a guest's details never feed Meta's advanced matching.
const NO_AUTOFILL = 'off';

type GuestTerms = {
  docs: ResolvedTerms[];
  tagValues?: TermsTagValues;
  studioName: string;
};

export const GuestTermsCheckbox = ({
  guests,
  index,
  terms,
  onGround = false,
}: {
  guests: GuestDetailsForm;
  // The guest this box answers for; every guest when absent.
  index?: number;
  terms: GuestTerms;
  // Straight on the page background rather than in a guest card. The row
  // around it carries on-ground for the checkbox's focus ring and error.
  onGround?: boolean;
}) => {
  const { form, termsKey, shake, fields } = guests;
  const { setValue, clearErrors, control } = form;
  const values = useWatch({ control, name: 'guests' });
  const { errors } = useFormState({ control, name: 'guests' });
  const [openDoc, setOpenDoc] = useState<ResolvedTerms | null>(null);
  const indexes = index === undefined ? fields.map((_, i) => i) : [index];

  if (!termsKey || !terms.docs.length || !indexes.length) return null;

  const checked = indexes.every((i) => values[i]?.terms_key === termsKey);
  const error = indexes
    .map((i) => errors.guests?.[i]?.terms_key)
    .find((fieldError) => !!fieldError);

  return (
    <>
      <Checkbox
        checked={checked}
        onCheckedChange={(value) =>
          indexes.forEach((i) => {
            setValue(`guests.${i}.terms_key`, value === true ? termsKey : null);
            clearErrors(`guests.${i}.terms_key`);
          })
        }
        hasError={!!error}
        errorMessage={guestTermsErrorMessage(error?.message, indexes.length)}
        shake={error ? shake : 0}
        label={
          <span
            className={mergeClassNames(
              'font-normal',
              onGround ? 'text-ground-body' : 'text-gray-700'
            )}
          >
            {GUEST_TERMS_LINE.lead(terms.studioName, indexes.length)}{' '}
            <TermsDocLinks
              docs={terms.docs}
              onOpen={setOpenDoc}
              onGround={onGround}
            />
          </span>
        }
      />
      <TermsModal
        doc={openDoc}
        onClose={() => setOpenDoc(null)}
        tagValues={terms.tagValues}
      />
    </>
  );
};

export const GuestDetailsFields = ({
  guests,
  index,
  terms,
  usePortal,
}: {
  guests: GuestDetailsForm;
  index: number;
  terms?: GuestTerms;
  usePortal?: boolean;
}) => {
  const { form, shake } = guests;
  const { register, setValue, trigger, control } = form;
  const { errors } = useFormState({ control, name: `guests.${index}` });
  const fieldErrors = errors.guests?.[index];
  const [country, phone] = useWatch({
    control,
    name: [`guests.${index}.country_code`, `guests.${index}.phone`],
  });
  const [phoneBlurred, setPhoneBlurred] = useState(false);
  const showPhoneError = (phoneBlurred || shake > 0) && !!fieldErrors?.phone;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-5 md:flex-row">
        <div className="min-w-0 flex-1">
          <Input
            label="First name"
            isRequired
            autoComplete={NO_AUTOFILL}
            {...register(`guests.${index}.first_name`)}
            hasError={!!fieldErrors?.first_name}
            errorMessage={fieldErrors?.first_name?.message}
            shake={fieldErrors?.first_name ? shake : 0}
          />
        </div>
        <div className="min-w-0 flex-1">
          <Input
            label="Last name"
            isRequired
            autoComplete={NO_AUTOFILL}
            {...register(`guests.${index}.last_name`)}
            hasError={!!fieldErrors?.last_name}
            errorMessage={fieldErrors?.last_name?.message}
            shake={fieldErrors?.last_name ? shake : 0}
          />
        </div>
      </div>

      <Input
        label="Email"
        isRequired
        helperText="We'll send their booking here"
        type="email"
        inputMode="email"
        autoComplete={NO_AUTOFILL}
        {...register(`guests.${index}.email`)}
        hasError={!!fieldErrors?.email}
        errorMessage={fieldErrors?.email?.message}
        shake={fieldErrors?.email ? shake : 0}
      />

      <SmartPhoneInput
        countries={ALLOWED_COUNTRIES}
        country={country as Country}
        onCountryChange={(c) => {
          setValue(`guests.${index}.country_code`, c, { shouldDirty: true });
          setValue(`guests.${index}.phone`, '', { shouldDirty: true });
        }}
        phone={phone}
        onPhoneChange={(v) =>
          setValue(`guests.${index}.phone`, v, {
            shouldDirty: true,
            shouldValidate: !!fieldErrors?.phone,
          })
        }
        onBlur={() => {
          setPhoneBlurred(true);
          trigger(`guests.${index}.phone`);
        }}
        isRequired
        usePortal={usePortal}
        error={showPhoneError}
        errorText={showPhoneError ? fieldErrors?.phone?.message : undefined}
        shake={fieldErrors?.phone ? shake : 0}
      />

      {terms && (
        <GuestTermsCheckbox guests={guests} index={index} terms={terms} />
      )}
    </div>
  );
};

const GuestCardHeader = ({
  index,
  count,
  canRemove,
  onRemoveGuest,
  className,
}: {
  index: number;
  count: number;
  canRemove: boolean;
  onRemoveGuest: (index: number) => void;
  className?: string;
}) => (
  <div
    className={mergeClassNames(
      'flex items-center justify-between gap-4',
      className
    )}
  >
    <span className="text-sm font-semibold uppercase tracking-wide text-gray-500">
      {guestCardTitle(index, count)}
    </span>
    {canRemove && (
      <Button
        variant="secondary-gray"
        size="md"
        iconOnly
        aria-label="Remove guest"
        onClick={() => onRemoveGuest(index)}
      >
        <TrashDeleteIcon className="h-5 w-5" />
      </Button>
    )}
  </div>
);

// `cards` is the checkout's look, one card per guest with its title always
// shown; `stacked` sits inside a modal, so its fields never portal. Without
// `terms` the page asks for the guests' agreement itself.
export const GuestDetailsList = ({
  guests,
  maxGuests,
  terms,
  canRemove,
  onRemoveGuest,
  variant,
  disabled,
}: {
  guests: GuestDetailsForm;
  maxGuests: number;
  terms?: GuestTerms;
  canRemove: boolean;
  onRemoveGuest: (index: number) => void;
  variant: 'cards' | 'stacked';
  disabled?: boolean;
}) => {
  const count = guests.fields.length;

  return (
    <>
      {guests.fields.map((field, index) =>
        variant === 'cards' ? (
          <Card key={field.id} contentClassName="p-[18px] sm:p-5">
            <GuestCardHeader
              index={index}
              count={count}
              canRemove={canRemove}
              onRemoveGuest={onRemoveGuest}
              className="mb-5"
            />
            <GuestDetailsFields guests={guests} index={index} terms={terms} />
          </Card>
        ) : (
          <div key={field.id} className="flex flex-col gap-5">
            {index > 0 && <Separator />}
            {canRemove && (
              <GuestCardHeader
                index={index}
                count={count}
                canRemove
                onRemoveGuest={onRemoveGuest}
              />
            )}
            <GuestDetailsFields
              guests={guests}
              index={index}
              terms={terms}
              usePortal={false}
            />
          </div>
        )
      )}

      {guests.listError && (
        <p role="alert" className="text-sm text-error-600">
          {guests.listError}
        </p>
      )}

      {count < maxGuests && (
        <Button
          variant="secondary-gray"
          size="lg"
          fullWidth
          leadingIcon={<Plus size={20} />}
          onClick={guests.addGuest}
          disabled={disabled}
        >
          Add another guest
        </Button>
      )}
    </>
  );
};
